import { MotherDto, Config, VisitDto, UserInfoDto } from '@ecdlink/core';
import {
  EventRecordType,
  MotherChildVisitModelInput,
  MotherModelInput,
  Scalars,
  Visit,
  VisitModelInput,
} from '@ecdlink/graphql';
import { api } from '../axios.helper';
class MotherService {
  _accessToken: string;

  constructor(accessToken: string) {
    this._accessToken = accessToken;
  }

  async getMothers(
    visitType?: 'all' | 'overdue' | 'due'
  ): Promise<MotherDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetAllMothersForHealthCareWorker',
      variables: {
        visitType,
      },
    });

    if (response.status !== 200) {
      throw new Error('Getting Mothers failed - Server connection error');
    }

    return response.data.data.allMothersForHealthCareWorker;
  }

  async getMotherStatusInfoForIds(
    motherUserIds: string[]
  ): Promise<UserInfoDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'getMotherStatusInfoForIds',
      variables: {
        motherUserIds,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Getting getMotherStatusInfoForIds failed - Server connection error'
      );
    }
    return response.data.data.motherStatusInfoForIds;
  }

  async getAllVisitsForMotherIds(motherUserIds: string[]): Promise<VisitDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { allVisitsForMotherIds: VisitDto[] };
    }>(``, {
      id: 'GetAllVisitsForMotherIds',
      variables: {
        motherUserIds: motherUserIds,
      },
    });

    if (response.status !== 200) {
      throw new Error('Getting mother visits failed - Server connection error');
    }
    return response.data.data.allVisitsForMotherIds;
  }

  async updateMotherDeliveryDate(
    id: string,
    expectedDateOfDelivery: Scalars['DateTime'],
    motherVisits: MotherChildVisitModelInput[]
  ): Promise<MotherDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { updateMotherDeliveryDate: MotherDto };
      errors?: {};
    }>(``, {
      id: 'UpdateMotherDeliveryDate',
      variables: {
        id: id,
        expectedDateOfDelivery,
        motherVisits,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Updating mother delivered date failed - Server connection error'
      );
    }

    return response.data.data.updateMotherDeliveryDate;
  }

  async updateMother(id: string, input: MotherModelInput): Promise<MotherDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'updateMother',
      variables: {
        id: id,
        input: input,
      },
    });

    if (response.status !== 200) {
      throw new Error('Updating mother failed - Server connection error');
    }

    return response.data.data.updateMother;
  }

  async addMother(
    input: MotherModelInput,
    clientVisits: MotherChildVisitModelInput[]
  ): Promise<MotherDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'addMother',
      variables: {
        input: input,
        clientVisits: clientVisits,
      },
    });

    if (response.status !== 200) {
      throw new Error('Updating mother failed - Server connection error');
    }

    return response.data.data.createMother;
  }

  async addAdditionalVisitForMother(input: VisitModelInput): Promise<any> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addAdditionalVisitForMother: Visit };
      errors?: {};
    }>(``, {
      id: 'addAdditionalVisitForMother',
      variables: {
        input,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'add Additional Visit For Mother failed - Server connection error'
      );
    }

    return response.data.data.addAdditionalVisitForMother;
  }

  async getMotherVisits(id: string): Promise<VisitDto[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { motherVisits: VisitDto[] };
    }>(``, {
      id: 'GetMotherVisits',
      variables: {
        userId: id,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Getting Mothers visits failed - Server connection error'
      );
    }

    return response.data.data.motherVisits;
  }

  async getAllMotherEventRecordTypes(): Promise<EventRecordType[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { allEventRecordTypesForType: EventRecordType[] };
    }>(``, {
      id: 'GetAllEventRecordTypesForType',
      variables: {
        type: 'mother',
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Getting event record types failed - Server connection error'
      );
    }

    return response.data.data.allEventRecordTypesForType;
  }

  async updateMotherAddress(
    id: string,
    input: MotherModelInput
  ): Promise<MotherDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'updateMotherAddress',
      variables: {
        id: id,
        input: input,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Updating mother address failed - Server connection error'
      );
    }

    return response.data.data.updateMotherAddress;
  }

  async updateMotherContactDetails(
    id: string,
    input: MotherModelInput
  ): Promise<MotherDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'updateMotherContactDetails',
      variables: {
        id: id,
        input: input,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Updating mother contact details failed - Server connection error'
      );
    }

    return response.data.data.updateMotherContactDetails;
  }

  async syncMotherVisits(clientVisits: VisitDto[]): Promise<MotherDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'syncMotherVisits',
      variables: {
        clientVisits,
      },
    });

    if (response.status !== 200) {
      throw new Error('Syncing mother visits failed - Server connection error');
    }

    return response.data.data.syncMotherVisits;
  }
}

export default MotherService;
